class PdpVideoReview extends HTMLElement {
  connectedCallback() {
    this.autoplay     = this.dataset.autoplay === 'true';
    this.defaultMuted = this.dataset.muted === 'true';
    this._initVideos();
    this._initSliders();
  }

  _initVideos() {
    this.querySelectorAll('[data-video-wrap]').forEach(wrap => {
      this._setupWrap(wrap);
    });
  }

  _setupWrap(wrap) {
    const hasSwiper = !!wrap.querySelector('[data-pdv-swiper-root]');

    const getCurrentVideo = () => hasSwiper
      ? wrap.querySelector('.swiper-slide-active video[data-vid]')
      : wrap.querySelector('video[data-vid]');

    const playBtn = wrap.querySelector('[data-play-btn]');

    const play = () => {
      const v = getCurrentVideo();
      if (!v) return;
      v.muted = this.autoplay || this.defaultMuted;
      // lazy: trigger load if not started yet
      if (v.preload === 'none' || v.readyState === 0) {
        v.preload = 'auto';
        v.load();
        v.addEventListener('canplay', () => v.play().catch(() => {}), { once: true });
      } else {
        v.play().catch(() => {});
      }
      if (playBtn) playBtn.classList.add('pdp-video-review__play--hidden');
      wrap.classList.add('pdp-video-review__video-wrap--playing');
    };

    const pauseAll = () => {
      wrap.querySelectorAll('video[data-vid]').forEach(v => v.pause());
      if (playBtn) playBtn.classList.remove('pdp-video-review__play--hidden');
      wrap.classList.remove('pdp-video-review__video-wrap--playing');
    };

    // click to pause only when NOT in autoplay mode
    if (!this.autoplay) {
      wrap.addEventListener('click', (e) => {
        if (e.target.closest('[data-pdv-prev], [data-pdv-next], [data-play-btn]')) return;
        const v = getCurrentVideo();
        if (v && !v.paused) pauseAll();
      });
    }

    if (playBtn) playBtn.addEventListener('click', play);

    wrap.querySelectorAll('video[data-vid]').forEach(v => v.addEventListener('ended', pauseAll));

    // IntersectionObserver — autoplay on enter, pause on leave
    new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.autoplay) play();
        else if (!entry.isIntersecting) pauseAll();
      });
    }, { threshold: 0.5 }).observe(wrap);

    // expose for _initSliders
    wrap._play     = play;
    wrap._pauseAll = pauseAll;
  }

  _initSliders() {
    const wraps = Array.from(this.querySelectorAll('[data-pdv-swiper]'));
    if (!wraps.length) return;

    const autoplay = this.autoplay;

    wraps.forEach(wrap => {
      const root = wrap.querySelector('[data-pdv-swiper-root]');
      if (!root) return;

      new window.Swiper(root, {
        slidesPerView: 1,
        speed: 400,
        loop: true,
        spaceBetween: 12,
        navigation: {
          prevEl: wrap.querySelector('[data-pdv-prev]'),
          nextEl: wrap.querySelector('[data-pdv-next]'),
        },
        keyboard: { enabled: true, onlyInViewport: true },
        a11y: { enabled: true },
        on: {
          slideChange(swiper) {
            // pause previous slide's video
            const prevSlide = swiper.slides[swiper.previousIndex];
            prevSlide?.querySelector('video[data-vid]')?.pause();

            // preload active slide's video
            const activeSlide = swiper.slides[swiper.activeIndex];
            const activeVideo = activeSlide?.querySelector('video[data-vid]');
            if (activeVideo && activeVideo.preload === 'none') {
              activeVideo.preload = 'auto';
            }

            // update UI (show play button)
            wrap._pauseAll?.();

            // restart autoplay on new slide
            if (autoplay) {
              setTimeout(() => wrap._play?.(), 100);
            }
          },
          afterInit(swiper) {
            // preload active slide immediately
            const activeSlide = swiper.slides[swiper.activeIndex];
            const activeVideo = activeSlide?.querySelector('video[data-vid]');
            if (activeVideo && activeVideo.preload === 'none') {
              activeVideo.preload = 'auto';
            }

            // fix autoplay: Swiper is ready now, trigger play if visible
            if (autoplay) {
              const rect = wrap.getBoundingClientRect();
              if (rect.top < window.innerHeight && rect.bottom > 0) {
                setTimeout(() => wrap._play?.(), 50);
              }
            }
          },
        },
      });
    });
  }
}

customElements.define('pdp-video-review', PdpVideoReview);
